import React, { useState } from 'react';
import { User as UserIcon, Mail, Phone, MapPin, Calendar, Edit3, Save, X, Briefcase, Building2, Shield, Lock, Camera, ArrowRight } from 'lucide-react';
import { useAppContext } from '../../hooks/useAppContext';
import useAppStore from '../../store/useAppStore';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { arSA } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

const ModernProfileScreen: React.FC = () => {
    const { t, user, updateUser: updateContextUser, changePassword } = useAppContext();
    const { updateUser: updateStoreUser } = useAppStore();
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);

    // State for password change form
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [isPasswordSaving, setIsPasswordSaving] = useState(false);

    if (!user) return null;

    const [formData, setFormData] = useState({
        name: user.name,
        phone: user.phone,
        position: user.position,
        bio: 'مسؤول مبيعات متخصص في حلول الطاقة الشمسية للمشاريع الكبرى.',
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSave = () => {
        const updatedUserData = {
            id: user.id,
            name: formData.name,
            phone: formData.phone,
            position: formData.position,
        };

        updateStoreUser(updatedUserData);
        updateContextUser(updatedUserData);

        toast.success('تم حفظ التعديلات بنجاح');
        setIsEditing(false);
    };

    const handleCancel = () => {
        setFormData({
            name: user.name,
            phone: user.phone,
            position: user.position,
            bio: 'مسؤول مبيعات متخصص في حلول الطاقة الشمسية للمشاريع الكبرى.',
        });
        setIsEditing(false);
    }

    const handlePasswordChange = async () => {
        if (!currentPassword || !newPassword || !confirmNewPassword) {
            toast.error('الرجاء ملء جميع حقول كلمة المرور.');
            return;
        }
        if (newPassword !== confirmNewPassword) {
            toast.error('كلمتا المرور الجديدتان غير متطابقتين.');
            return;
        }
        if (newPassword.length < 6) {
            toast.error('يجب أن تكون كلمة المرور الجديدة 6 أحرف على الأقل.');
            return;
        }

        setIsPasswordSaving(true);
        const success = await changePassword(user.id, currentPassword, newPassword);
        setIsPasswordSaving(false);

        if (success) {
            setCurrentPassword('');
            setNewPassword('');
            setConfirmNewPassword('');
        }
    };

    const formattedJoinDate = format(new Date(user.joinDate), 'PPP', { locale: arSA });

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold modern-gradient-text mb-2">الملف الشخصي</h1>
                    <p className="text-slate-400">إدارة معلوماتك الشخصية وإعدادات الأمان</p>
                </div>
                <button
                    onClick={() => navigate('/')}
                    className="px-4 py-2 rounded-xl glass-card hover:bg-white/10 text-slate-300 transition-all flex items-center gap-2"
                >
                    <ArrowRight size={18} />
                    <span>رجوع</span>
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column: User Card */}
                <div className="lg:col-span-1 space-y-6">
                    <div className="glass-card p-8 rounded-2xl flex flex-col items-center text-center relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-blue-600/20 to-transparent"></div>

                        <div className="relative mb-6 group">
                            <div className="w-32 h-32 rounded-full border-4 border-slate-800 shadow-xl bg-slate-700 flex items-center justify-center overflow-hidden">
                                <UserIcon size={64} className="text-slate-400" />
                            </div>
                            <button className="absolute bottom-0 right-0 p-2 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-500 transition-colors">
                                <Camera size={16} />
                            </button>
                        </div>

                        {isEditing ? (
                            <div className="w-full space-y-3 mb-6">
                                <input
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    className="w-full bg-slate-800/50 border border-slate-700 rounded-xl py-2 px-4 text-center text-white font-bold text-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                                />
                                <input
                                    name="position"
                                    value={formData.position}
                                    onChange={handleInputChange}
                                    className="w-full bg-slate-800/50 border border-slate-700 rounded-xl py-2 px-4 text-center text-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                                />
                            </div>
                        ) : (
                            <div className="mb-6">
                                <h2 className="text-2xl font-bold text-white mb-2">{formData.name}</h2>
                                <span className="px-3 py-1 rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/30 text-sm">
                                    {formData.position}
                                </span>
                            </div>
                        )}

                        <div className="w-full space-y-4 text-sm text-slate-400 border-t border-white/5 pt-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <Mail size={16} className="text-blue-400" />
                                    <span>البريد الإلكتروني</span>
                                </div>
                                <span className="text-slate-300" dir="ltr">{user.email}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <Phone size={16} className="text-purple-400" />
                                    <span>رقم الهاتف</span>
                                </div>
                                {isEditing ? (
                                    <input
                                        name="phone"
                                        dir="ltr"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        className="w-32 bg-slate-800/50 border border-slate-700 rounded-lg py-1 px-2 text-right text-white text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                                    />
                                ) : (
                                    <span className="text-slate-300" dir="ltr">{formData.phone}</span>
                                )}
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <MapPin size={16} className="text-emerald-400" />
                                    <span>الفرع</span>
                                </div>
                                <span className="text-slate-300">{user.branch}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <Calendar size={16} className="text-amber-400" />
                                    <span>تاريخ الانضمام</span>
                                </div>
                                <span className="text-slate-300">{formattedJoinDate}</span>
                            </div>
                        </div>

                        <div className="w-full mt-8">
                            {!isEditing ? (
                                <button
                                    onClick={() => setIsEditing(true)}
                                    className="w-full py-3 rounded-xl bg-slate-800 text-white font-medium hover:bg-slate-700 transition-colors flex items-center justify-center gap-2"
                                >
                                    <Edit3 size={18} />
                                    <span>تعديل المعلومات</span>
                                </button>
                            ) : (
                                <div className="flex gap-3">
                                    <button
                                        onClick={handleCancel}
                                        className="flex-1 py-3 rounded-xl bg-slate-800 text-slate-300 hover:bg-slate-700 transition-colors flex items-center justify-center gap-2"
                                    >
                                        <X size={18} />
                                        <span>إلغاء</span>
                                    </button>
                                    <button
                                        onClick={handleSave}
                                        className="flex-1 py-3 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-500 transition-colors flex items-center justify-center gap-2"
                                    >
                                        <Save size={18} />
                                        <span>حفظ</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Column: Details & Security */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Bio */}
                    <div className="glass-card p-6 rounded-2xl">
                        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                            <UserIcon size={20} className="text-blue-400" />
                            نبذة شخصية
                        </h3>
                        {isEditing ? (
                            <textarea
                                name="bio"
                                value={formData.bio}
                                onChange={handleInputChange}
                                className="w-full bg-slate-800/50 border border-slate-700 rounded-xl py-3 px-4 text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 min-h-[100px]"
                            />
                        ) : (
                            <p className="text-slate-300 leading-relaxed">{formData.bio}</p>
                        )}
                    </div>

                    {/* Work Info */}
                    <div className="glass-card p-6 rounded-2xl">
                        <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                            <Briefcase size={20} className="text-purple-400" />
                            معلومات العمل
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="p-4 bg-slate-800/30 rounded-xl border border-white/5 flex items-center gap-4">
                                <div className="p-3 bg-blue-500/20 text-blue-400 rounded-lg">
                                    <Building2 size={24} />
                                </div>
                                <div>
                                    <p className="text-xs text-slate-400 mb-1">القسم</p>
                                    <p className="text-white font-semibold">{user.department}</p>
                                </div>
                            </div>
                            <div className="p-4 bg-slate-800/30 rounded-xl border border-white/5 flex items-center gap-4">
                                <div className="p-3 bg-purple-500/20 text-purple-400 rounded-lg">
                                    <MapPin size={24} />
                                </div>
                                <div>
                                    <p className="text-xs text-slate-400 mb-1">الفرع</p>
                                    <p className="text-white font-semibold">{user.branch}</p>
                                </div>
                            </div>
                            <div className="p-4 bg-slate-800/30 rounded-xl border border-white/5 flex items-center gap-4">
                                <div className="p-3 bg-emerald-500/20 text-emerald-400 rounded-lg">
                                    <Shield size={24} />
                                </div>
                                <div>
                                    <p className="text-xs text-slate-400 mb-1">رقم الموظف</p>
                                    <p className="text-white font-semibold">{user.employeeId}</p>
                                </div>
                            </div>
                            <div className="p-4 bg-slate-800/30 rounded-xl border border-white/5 flex items-center gap-4">
                                <div className="p-3 bg-amber-500/20 text-amber-400 rounded-lg">
                                    <Calendar size={24} />
                                </div>
                                <div>
                                    <p className="text-xs text-slate-400 mb-1">تاريخ الانضمام</p>
                                    <p className="text-white font-semibold">{formattedJoinDate}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Security */}
                    <div className="glass-card p-6 rounded-2xl">
                        <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                            <Lock size={20} className="text-red-400" />
                            إعدادات الأمان
                        </h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-2">كلمة المرور الحالية</label>
                                <div className="relative">
                                    <Lock className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                                    <input
                                        type="password"
                                        value={currentPassword}
                                        onChange={e => setCurrentPassword(e.target.value)}
                                        className="w-full bg-slate-800/50 border border-slate-700 rounded-xl py-2.5 pr-10 pl-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-400 mb-2">كلمة المرور الجديدة</label>
                                    <div className="relative">
                                        <Lock className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                                        <input
                                            type="password"
                                            value={newPassword}
                                            onChange={e => setNewPassword(e.target.value)}
                                            className="w-full bg-slate-800/50 border border-slate-700 rounded-xl py-2.5 pr-10 pl-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-400 mb-2">تأكيد كلمة المرور الجديدة</label>
                                    <div className="relative">
                                        <Lock className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                                        <input
                                            type="password"
                                            value={confirmNewPassword}
                                            onChange={e => setConfirmNewPassword(e.target.value)}
                                            className="w-full bg-slate-800/50 border border-slate-700 rounded-xl py-2.5 pr-10 pl-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="pt-4 flex justify-end">
                                <button
                                    onClick={handlePasswordChange}
                                    disabled={isPasswordSaving}
                                    className="px-6 py-2.5 rounded-xl bg-red-600/80 text-white font-medium hover:bg-red-500 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isPasswordSaving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save size={18} />}
                                    <span>تغيير كلمة المرور</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ModernProfileScreen;
